function createTraditionalWatermarkDataUrl(width: number, height: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Watermark canvas context unavailable');

  const startX = canvas.width * 0.2;
  const startY = canvas.height * 0.2;

  // Soft corner glow.
  const glow = ctx.createRadialGradient(canvas.width, canvas.height, 0, canvas.width, canvas.height, canvas.width * 0.9);
  glow.addColorStop(0, 'rgba(45, 141, 204, 0.09)');
  glow.addColorStop(0.45, 'rgba(45, 141, 204, 0.045)');
  glow.addColorStop(1, 'rgba(45, 141, 204, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Clean angled bands.
  ctx.save();
  ctx.translate(canvas.width * 0.78, canvas.height * 0.78);
  ctx.rotate((-35 * Math.PI) / 180);
  const bandWidths = [22, 16, 10];
  bandWidths.forEach((h, i) => {
    ctx.fillStyle = `rgba(45, 141, 204, ${0.1 - i * 0.025})`;
    const y = i * 24;
    ctx.fillRect(-canvas.width * 0.75, y, canvas.width * 1.2, h);
  });
  ctx.restore();

  // Subtle dot matrix for premium-paper feel.
  ctx.save();
  ctx.fillStyle = 'rgba(45, 141, 204, 0.07)';
  const dotGap = 16;
  for (let y = startY; y < canvas.height; y += dotGap) {
    for (let x = startX; x < canvas.width; x += dotGap) {
      const dx = canvas.width - x;
      const dy = canvas.height - y;
      if (dx * dx + dy * dy <= (canvas.width * 0.72) * (canvas.width * 0.72)) {
        ctx.beginPath();
        ctx.arc(x, y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  ctx.restore();

  return canvas.toDataURL('image/png');
}

function getCanvasContentHeight(canvas: HTMLCanvasElement): number {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.height;

  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const xStep = Math.max(1, Math.floor(width / 420));
  const scanStart = Math.floor(width * 0.03);
  const scanEnd = Math.max(scanStart + 1, Math.floor(width * 0.97));
  const minMeaningfulHits = 2;

  for (let y = height - 1; y >= 0; y -= 1) {
    const rowStart = y * width * 4;
    let nonWhiteHits = 0;
    for (let x = scanStart; x < scanEnd; x += xStep) {
      const i = rowStart + x * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      // Detect non-empty pixels while ignoring pure/near-white background.
      if (a > 8 && (r < 248 || g < 248 || b < 248)) {
        nonWhiteHits += 1;
        if (nonWhiteHits >= minMeaningfulHits) {
          return Math.min(height, y + 3); // tiny safety buffer
        }
      }
    }
  }

  return 0;
}

/** Terms / intro / signature blocks that should not leave a small stub on the previous page. */
const KEEP_TOGETHER_SELECTOR =
  '[data-pdf-keep-together], .pdf-intro-section, .pdf-signature-section, .pdf-custom-body-section, .pdf-keep-together';

/** If less than this fraction of a keep-together block fits on the page, move the whole block. */
const KEEP_TOGETHER_MIN_PAGE_FRACTION = 0.3;

type KeepTogetherBlock = { topPx: number; bottomPx: number; heightPx: number };

function collectKeepTogetherBlocks(
  bodyEl: HTMLElement,
  bodyRect: DOMRect,
  renderScale: number
): KeepTogetherBlock[] {
  return Array.from(bodyEl.querySelectorAll<HTMLElement>(KEEP_TOGETHER_SELECTOR)).map((el) => {
    const r = el.getBoundingClientRect();
    const topPx = (r.top - bodyRect.top) * renderScale;
    const bottomPx = (r.bottom - bodyRect.top) * renderScale;
    return { topPx, bottomPx, heightPx: Math.max(1, bottomPx - topPx) };
  });
}

/**
 * If a slice would leave only a small stub of a terms/intro section on this page
 * (~20–30%) with most of it on the next page, break before that section instead.
 */
function adjustSliceEndForKeepTogether(
  renderedBodyPx: number,
  proposedEnd: number,
  blocks: KeepTogetherBlock[],
  pageCapacityPx: number
): number {
  let sliceEnd = proposedEnd;
  for (const block of blocks) {
    const startsOnThisPage = block.topPx >= renderedBodyPx - 0.5 && block.topPx < sliceEnd;
    const endsAfterSlice = block.bottomPx > sliceEnd + 0.5;
    if (!startsOnThisPage || !endsAfterSlice) continue;

    const onThisPage = sliceEnd - block.topPx;
    const fractionOnThisPage = onThisPage / block.heightPx;
    if (fractionOnThisPage > 0 && fractionOnThisPage < KEEP_TOGETHER_MIN_PAGE_FRACTION) {
      const nextStart = Math.floor(block.topPx);
      if (nextStart > renderedBodyPx) {
        sliceEnd = nextStart;
      }
    } else if (
      fractionOnThisPage < 1 &&
      block.heightPx <= pageCapacityPx &&
      block.topPx > renderedBodyPx
    ) {
      sliceEnd = Math.floor(block.topPx);
    }
  }
  return Math.max(renderedBodyPx + 1, Math.min(sliceEnd, proposedEnd));
}

export async function exportElementToPdf(element: HTMLElement, filename: string): Promise<void> {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf')
  ]);

  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
  const pdfWidthMm = 210;
  const pdfHeightMm = 297;
  const renderScale = 2;
  const footerTopGapMm = 4;
  const footerBottomGapMm = 4; // ~15px visual breathing room at page bottom
  const headerBottomGapMm = 6; // ~23px breathing room below header on each page

  // Render from an offscreen A4-sized clone for consistent quality.
  const a4WidthPx = 794; // 210mm at 96dpi

  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-100000px';
  host.style.top = '0';
  host.style.width = `${a4WidthPx}px`;
  host.style.background = '#ffffff';
  host.style.zIndex = '-1';

  const clone = element.cloneNode(true) as HTMLElement;
  clone.style.width = `${a4WidthPx}px`;
  clone.style.maxWidth = 'none';
  clone.style.minHeight = '1123px'; // 297mm at 96dpi
  clone.style.margin = '0';
  clone.style.boxShadow = 'none';
  clone.style.border = 'none';

  host.appendChild(clone);
  clone.setAttribute('data-pdf-exporting', 'true');
  const headerPaint = clone.querySelector<HTMLElement>('.pdf-brand-header');
  const leftPaint = clone.querySelector<HTMLElement>('.pdf-brand-left');
  const logoPaint = clone.querySelector<HTMLElement>('.pdf-brand-logo-img');
  if (headerPaint) headerPaint.style.backgroundColor = '#288bcc';
  if (leftPaint) leftPaint.style.backgroundColor = '#288bcc';
  if (logoPaint) logoPaint.style.backgroundColor = 'transparent';
  const exportStyle = document.createElement('style');
  exportStyle.textContent = `
    /* Remove decorative pseudo-elements */
    [data-pdf-exporting='true'] .pdf-page::before,
    [data-pdf-exporting='true'] .pdf-page::after,
    [data-pdf-exporting='true'] .pdf-body-content::before,
    [data-pdf-exporting='true'] .pdf-body-content::after {
      content: none !important;
    }

    /* Force desktop layout for consistent PDF regardless of viewport size */
    [data-pdf-exporting='true'] {
      width: ${a4WidthPx}px !important;
      min-width: ${a4WidthPx}px !important;
      max-width: none !important;
    }

    [data-pdf-exporting='true'] .invoice-meta-grid {
      grid-template-columns: 1fr 1fr !important;
    }

    [data-pdf-exporting='true'] .invoice-header {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
    }

    [data-pdf-exporting='true'] .pdf-brand-header {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      min-height: 118px !important;
      background: #288bcc !important;
    }

    [data-pdf-exporting='true'] .pdf-brand-left {
      background: #288bcc !important;
    }

    [data-pdf-exporting='true'] .pdf-brand-logo-img {
      width: 250px !important;
      background: transparent !important;
    }

    [data-pdf-exporting='true'] .pdf-body-content {
      padding: 20px 28px 28px !important;
      margin-left: 12px !important;
      border-left: 6px solid #2d8dcc !important;
    }

    [data-pdf-exporting='true'] .invoice-title {
      font-size: 1.6rem !important;
    }

    [data-pdf-exporting='true'] .company-details {
      font-size: 0.9rem !important;
      text-align: right !important;
    }

    [data-pdf-exporting='true'] .invoice-table thead th,
    [data-pdf-exporting='true'] .invoice-table tbody td {
      padding: 12px !important;
    }

    [data-pdf-exporting='true'] .meta-group {
      padding: 16px !important;
    }

    [data-pdf-exporting='true'] .invoice-compliance-footer {
      font-size: 12px !important;
      padding: 12px 16px !important;
      line-height: 1.6 !important;
      text-align: center !important;
      font-weight: 600 !important;
    }

    [data-pdf-exporting='true'] .invoice-footer-note {
      font-size: 13px !important;
      text-align: center !important;
      line-height: 1.6 !important;
    }
  `;
  host.appendChild(exportStyle);
  document.body.appendChild(host);

  try {
    const captureOptions = {
      scale: renderScale,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      imageTimeout: 0
    };

    const headerEl = clone.querySelector<HTMLElement>('[data-pdf-header], .pdf-brand-header');
    const footerEl = clone.querySelector<HTMLElement>('[data-pdf-footer], .invoice-compliance-footer');
    const bodyEl = clone.querySelector<HTMLElement>('[data-pdf-body], .pdf-body-content') || clone;

    // Fallback to classic full-page slicing if we cannot isolate fixed header/footer.
    if (!headerEl || !footerEl) {
      const canvas = await html2canvas(clone, captureOptions);
      const pxPerMm = canvas.width / pdfWidthMm;
      const pageHeightPx = Math.floor(pdfHeightMm * pxPerMm);
      const effectiveCanvasHeight = getCanvasContentHeight(canvas);
      let rendered = 0;
      let page = 0;

      while (rendered < effectiveCanvasHeight) {
        const sliceHeight = Math.min(pageHeightPx, effectiveCanvasHeight - rendered);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const ctx = pageCanvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context unavailable');

        ctx.drawImage(
          canvas,
          0,
          rendered,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        const img = pageCanvas.toDataURL('image/png');
        const sliceHeightMm = sliceHeight / pxPerMm;

        if (page > 0) pdf.addPage();
        pdf.addImage(img, 'PNG', 0, 0, pdfWidthMm, sliceHeightMm, undefined, 'FAST');

        rendered += sliceHeight;
        page += 1;
      }

      pdf.save(`${filename}.pdf`);
      return;
    }

    const cloneRect = clone.getBoundingClientRect();
    const headerRect = headerEl.getBoundingClientRect();
    const footerRect = footerEl.getBoundingClientRect();
    const bodyRect = bodyEl.getBoundingClientRect();

    const headerCanvas = await html2canvas(headerEl, captureOptions);
    const footerCanvas = await html2canvas(footerEl, captureOptions);

    // Keep footer fixed on every page; it should not be part of body slicing.
    const previousFooterDisplay = footerEl.style.display;
    footerEl.style.display = 'none';
    const bodyCanvas = await html2canvas(bodyEl, captureOptions);
    footerEl.style.display = previousFooterDisplay;

    const pxPerMm = (cloneRect.width * renderScale) / pdfWidthMm;

    const headerHeightMm = headerCanvas.height / pxPerMm;
    const footerHeightMm = footerCanvas.height / pxPerMm;
    const contentHeightMm =
      pdfHeightMm - headerHeightMm - headerBottomGapMm - footerHeightMm - footerTopGapMm - footerBottomGapMm;

    if (contentHeightMm <= 0) {
      throw new Error('Header and footer are too tall for the page height.');
    }

    const headerXmm = ((headerRect.left - cloneRect.left) * renderScale) / pxPerMm;
    const footerXmm = ((footerRect.left - cloneRect.left) * renderScale) / pxPerMm;
    const bodyXmm = ((bodyRect.left - cloneRect.left) * renderScale) / pxPerMm;

    const headerWidthMm = headerCanvas.width / pxPerMm;
    const footerWidthMm = footerCanvas.width / pxPerMm;
    const bodyWidthMm = bodyCanvas.width / pxPerMm;

    const contentHeightPx = Math.floor(contentHeightMm * pxPerMm);
    const effectiveBodyHeightPx = getCanvasContentHeight(bodyCanvas);
    const paginatedBodyHeightPx = Math.max(1, Math.min(bodyCanvas.height, effectiveBodyHeightPx));
    const keepBlocks = collectKeepTogetherBlocks(bodyEl, bodyRect, renderScale);
    let renderedBodyPx = 0;
    let page = 0;

    const headerImg = headerCanvas.toDataURL('image/png');
    const footerImg = footerCanvas.toDataURL('image/png');
    const watermarkWidthMm = bodyWidthMm * 0.66;
    const watermarkHeightMm = contentHeightMm * 0.66;
    const watermarkXmm = bodyXmm + bodyWidthMm - watermarkWidthMm + 2;
    const watermarkYmm = headerHeightMm + contentHeightMm - watermarkHeightMm + 2;
    const watermarkImg = createTraditionalWatermarkDataUrl(
      Math.floor(bodyCanvas.width * 0.66),
      Math.floor(contentHeightPx * 0.66)
    );

    while (renderedBodyPx < paginatedBodyHeightPx) {
      const proposedEnd = Math.min(renderedBodyPx + contentHeightPx, paginatedBodyHeightPx);
      const sliceEnd = adjustSliceEndForKeepTogether(
        renderedBodyPx,
        proposedEnd,
        keepBlocks,
        contentHeightPx
      );
      const sliceHeight = Math.max(1, sliceEnd - renderedBodyPx);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = bodyCanvas.width;
      pageCanvas.height = sliceHeight;

      const ctx = pageCanvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context unavailable');

      ctx.drawImage(
        bodyCanvas,
        0,
        renderedBodyPx,
        bodyCanvas.width,
        sliceHeight,
        0,
        0,
        bodyCanvas.width,
        sliceHeight
      );

      const bodySliceImg = pageCanvas.toDataURL('image/png');
      const sliceHeightMm = sliceHeight / pxPerMm;

      if (page > 0) pdf.addPage();

      pdf.addImage(headerImg, 'PNG', headerXmm, 0, headerWidthMm, headerHeightMm, undefined, 'FAST');
      pdf.addImage(bodySliceImg, 'PNG', bodyXmm, headerHeightMm + headerBottomGapMm, bodyWidthMm, sliceHeightMm, undefined, 'FAST');
      pdf.addImage(
        watermarkImg,
        'PNG',
        watermarkXmm,
        watermarkYmm,
        watermarkWidthMm,
        watermarkHeightMm,
        undefined,
        'FAST'
      );
      pdf.addImage(
        footerImg,
        'PNG',
        footerXmm,
        pdfHeightMm - footerHeightMm - footerBottomGapMm,
        footerWidthMm,
        footerHeightMm,
        undefined,
        'FAST'
      );

      renderedBodyPx = sliceEnd;
      page += 1;
    }

    pdf.save(`${filename}.pdf`);
  } finally {
    host.remove();
  }
}
